import { Injectable } from '@angular/core';
import { EventEmitter } from '@angular/core';
import { Idle, DEFAULT_INTERRUPTSOURCES } from '@ng-idle/core';
import { Keepalive } from '@ng-idle/keepalive';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ConfigService } from '../../services/config.service';
import { UserService } from '../../services/user.service';
import { IdleComponent } from '../../idle/idle/idle.component';
import { BehaviorSubject } from 'rxjs';

@Injectable({
	providedIn: 'root'
})
export class IdleService {
	idleLogoutEvent: EventEmitter<{ eventType: string, forced: boolean, timestamp: Date }> = new EventEmitter();

	modalOpen: boolean = false;
	modalRef: any;

	private countdown$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
	private timedout$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

	constructor(
		private idle: Idle,
		private keepalive: Keepalive,
		private configService: ConfigService,
		private userService: UserService,
		private modalService: NgbModal
	) {
		this.setupIdleDetection();
	}

	private setupIdleDetection() {
		const idleTime = this.configService.getConfig().idleManager.idleTime;
		const timeout = this.configService.getConfig().idleManager.timeout;
		const keepAliveTime = this.configService.getConfig().idleManager.keepAliveTime;

		// Amount of time to wait before idle starts
		this.idle.setIdle(idleTime);

		//Countdown time before timeout event
		this.idle.setTimeout(timeout);

		// User events that reset the idle timer
		// Defaults include mousemove, keydown, DOMMouseScroll, mousewheel, mousedown, touchstart, touchmove and scroll.
		this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

		// Keepalive interval setup
		this.keepalive.interval(keepAliveTime);

		// Fires when the user becomes idle
		this.idle.onIdleStart.subscribe(() => {
			console.log('User is idle.');
		});

		// Fires each second for the duration that was provided to the setTimeout method (countdown)
		this.idle.onTimeoutWarning.subscribe((countdown: number) => {
			if (!this.userService.user.loggedin) {
				console.log('Timeout warning: User not logged in');
				this.reset();
			} else {
				console.log(`Timeout warning: ${countdown} seconds remaining`);
				this.countdown$.next(countdown); // Updates countdown dynamically
				if (!this.modalOpen) {
					this.showIdleWarning();
				}
			}
		});

		// Fires when the specified amount of time has expired
		this.idle.onTimeout.subscribe(() => {
			console.log('User session timed out!');
			this.timedout$.next(true);
			this.logout(false);
		});

		// Start watching for idle activity
		this.idle.watch();
	}

	reset() {
		console.log('Reset idle monitor');
		this.timedout$.next(false);
		this.idle.watch();
	}

	private logout(forced: boolean) {
		this.idleLogoutEvent.emit({
			eventType: 'idleLogout',
			forced: forced,
			timestamp: new Date(),
		});

		if (this.modalOpen) {
			this.modalRef.close({
				result: { reason: 'timeout' },
				timestamp: new Date(),
			});
		}
		
		this.timedout$.next(false);
	}

	private showIdleWarning() {
		this.modalOpen = true;

		this.modalRef = this.modalService.open(IdleComponent, {
			size: 'sm'
		});

		this.modalRef.componentInstance.countdown$ = this.countdown$;
		this.modalRef.componentInstance.timedout$ = this.timedout$;

		this.modalRef.result.then(
			(payload: any) => {
				this.modalOpen = false;
				
				if (payload.result?.reason === 'timeout') {
					this.reset();
				}			
				else if (payload.result.caller?.action === 'logout') {
					console.log('Modal dismissed, logout');
					this.logout(true);
					this.reset();
				}
			},
			(reason: any) => {
				console.log('Modal dismissed, extend session');
				this.modalOpen = false;
				this.reset();
			}
		);
	}
}